// Notification settings widget: data-365="notification-settings"
// Lets a signed-in customer manage how 365 contacts them — email + SMS receipts/reminders — natively
// on the venue's own site. These are ACCOUNT-level preferences (not per-venue). Reads/writes the same
// endpoint the 365 web account page uses: GET/PUT /api/auth/notification-preferences. The full pref
// object is round-tripped (only 4 customer-facing toggles are shown; the rest pass through untouched).
// Minor gating is enforced SERVER-side (SMS force-off for under-13 / parental-consent accounts), so the
// re-render after Save reflects the server's decision. Login-gated read, same model as member-card.
import { Api } from '../api';
import { Auth, AuthRequiredError } from '../auth';
import type { EmbedConfig, ThemeOptions } from '../types';
import { createRoot, h, footer, clear, renderLoading, signOutRow } from '../ui';

interface Prefs {
  emailReceipts: boolean;
  gameReminders: boolean;
  marketingEmails: boolean;
  tournamentUpdates: boolean;
  gameCompletionAlerts: boolean;
  statsNotifications: boolean;
  recordingPromotions: boolean;
  smsReceipts: boolean;
  smsReminders: boolean;
  smsMarketing: boolean;
  pushEnabled: boolean;
  pushStreamLive: boolean;
  pushRecordingReady: boolean;
  pushGameReminder: boolean;
  pushTournamentUpdates: boolean;
}

interface ToggleDef { key: keyof Prefs; label: string; desc: string; sms?: boolean; }
const TOGGLES: ToggleDef[] = [
  { key: 'emailReceipts', label: 'Email receipts & confirmations', desc: 'Booking and purchase confirmations, emailed to you.' },
  { key: 'gameReminders', label: 'Game & booking reminders', desc: 'A heads-up by email before your upcoming games and bookings.' },
  { key: 'smsReceipts', label: 'Text me receipts & confirmations', desc: 'Get booking confirmations by SMS.', sms: true },
  { key: 'smsReminders', label: 'Text me reminders', desc: 'Get a reminder text before your bookings. (Coming soon — your choice is saved now.)', sms: true },
];

export function mountNotificationSettings(mount: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth, theme: ThemeOptions): void {
  const { root } = createRoot(mount, theme);
  const pending = auth.peekPending<{ t?: string }>();
  if (pending && pending.t === 'notifications') auth.clearPending();
  void load(root, cfg, api, auth);
}

async function load(root: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth): Promise<void> {
  clear(root);
  root.append(h('h3', undefined, 'Notifications'));

  if (!auth.isLoggedIn) {
    root.append(h('div', { class: 'e365-muted', style: 'margin:-4px 0 10px' }, 'Sign in to choose how we keep you posted.'));
    const login = h('button', { class: 'e365-btn' }, 'Sign in') as HTMLButtonElement;
    login.addEventListener('click', () => auth.redirectToLogin({ t: 'notifications' }));
    root.append(login, footer());
    return;
  }

  const host = h('div');
  root.append(host);
  renderLoading(host, 'Notifications');

  let prefs: Prefs;
  try {
    prefs = await api.get<Prefs>('/api/auth/notification-preferences');
  } catch (e) {
    clear(host);
    if (e instanceof AuthRequiredError) {
      host.append(h('div', { class: 'e365-muted', style: 'margin:-4px 0 10px' }, 'Please sign in again to manage your notifications.'));
      const login = h('button', { class: 'e365-btn' }, 'Sign in') as HTMLButtonElement;
      login.addEventListener('click', () => auth.redirectToLogin({ t: 'notifications' }));
      host.append(login, footer());
    } else {
      host.append(h('div', { class: 'e365-alert error' }, 'We couldn’t load your notification settings. Please try again.'));
      const retry = h('button', { class: 'e365-btn' }, 'Try again') as HTMLButtonElement;
      retry.addEventListener('click', () => void load(root, cfg, api, auth));
      host.append(retry, footer());
    }
    return;
  }

  renderPrefs(root, host, cfg, api, auth, prefs, false);
}

function renderPrefs(root: HTMLElement, host: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth, prefs: Prefs, justSaved: boolean): void {
  clear(host);
  const working: Prefs = { ...prefs }; // round-trip the full object; only the 4 toggles below are user-editable

  host.append(h('div', { class: 'e365-muted', style: 'margin:-4px 0 12px; font-size:.85rem;' },
    'Choose how we keep you posted about your bookings and games. These apply to your 365 account.'));

  if (justSaved) host.append(h('div', { class: 'e365-alert info', style: 'margin-top:0' }, '✓ Notification settings saved.'));

  let anySms = false;
  for (const t of TOGGLES) {
    if (t.sms) anySms = true;
    const row = h('label', {
      style: 'display:flex; align-items:flex-start; gap:10px; padding:10px 0; border-bottom:1px solid var(--e365-border); cursor:pointer;',
    });
    const cb = h('input', { type: 'checkbox', style: 'margin-top:3px; width:18px; height:18px; flex:none; accent-color:var(--e365-primary);' }) as HTMLInputElement;
    cb.checked = !!working[t.key];
    cb.addEventListener('change', () => { working[t.key] = cb.checked; });
    const text = h('div');
    text.append(h('div', { style: 'font-weight:600;' }, t.label));
    text.append(h('div', { class: 'e365-muted', style: 'font-size:.8rem;' }, t.desc));
    row.append(cb, text);
    host.append(row);
  }

  if (anySms) host.append(h('div', { class: 'e365-muted', style: 'font-size:.72rem; margin-top:8px;' },
    'Msg & data rates may apply. Reply STOP to any text to opt out. Texts are only sent for venues that have SMS enabled.'));

  const save = h('button', { class: 'e365-btn' }, 'Save') as HTMLButtonElement;
  save.addEventListener('click', () => void doSave(root, host, cfg, api, auth, working, save));
  host.append(save);

  host.append(
    signOutRow(auth.profile?.email, () => { auth.logout(); void load(root, cfg, api, auth); }),
    footer(),
  );
}

async function doSave(root: HTMLElement, host: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth, working: Prefs, btn: HTMLButtonElement): Promise<void> {
  btn.disabled = true;
  btn.textContent = 'Saving…';
  try {
    const updated = await api.put<Prefs>('/api/auth/notification-preferences', working);
    renderPrefs(root, host, cfg, api, auth, updated, true); // reflects server-side minor gating
  } catch (e) {
    btn.disabled = false;
    btn.textContent = 'Save';
    if (e instanceof AuthRequiredError) { auth.redirectToLogin({ t: 'notifications' }); return; }
    window.alert('We couldn’t save your settings. Please try again.');
  }
}
